import {
  createAsyncThunk,
  createSlice,
  createSelector
} from '@reduxjs/toolkit'
import { singularize } from '@polleverywhere/core/lib/util'
import store from '@polleverywhere/redux'
import rootFolders from '@polleverywhere/redux/slices/explorer/root_folders'
import activityApi from '@polleverywhere/redux/apis/activity'
import {
  getSlice,
  selectItemId,
  selectItemIdParts
} from '@polleverywhere/redux/slices/explorer'
import activityFactory from '@polleverywhere/core/lib/activity_factory'
import { slice as appSlice } from '../../slices/app'
import { generateActivityPath } from '../../util/compute_activity_route'
import Skeleton from '../../models/skeleton'
import { cloneDeep } from 'lodash'

const sliceName = 'app-activities'
const selectors = {}
const thunks = {}

export const explorer = getSlice(`${sliceName}/explorer`)

store.addReducers({
  [explorer.name]: explorer.reducer
})

function stubFolder (id) {
  return { id, name: 'Stub' }
}

function setupView (view, dispatch) {
  const { role, mode } = store.getState()[appSlice.name]
  const isTouchDevice = window?.navigator?.maxTouchPoints > 0
  const sortOrder = 'asc'
  const sortField = 'order'

  switch (view) {
    case 'activities':
      if (role === 'agave') {
        dispatch(explorer.initAgaveView({
          disableTooltips: isTouchDevice,
          sortOrder,
          sortField
        }))
      } else {
        dispatch(explorer.initActivitiesView({
          // Only enable drag n drop on non-touch device
          dragActions: !isTouchDevice,
          // Tool tips are quirky on touch devices, so disable them
          disableTooltips: isTouchDevice,
          sortOrder,
          sortField,
          fields: [],
          // Hide the 'Order' column on touch devices as ordering isn't possible
          presentationOrder: !isTouchDevice
        }))

        const actions = mode === 'integration' || role === 'slideware'
          ? ['insertSlides', 'move', 'copy', 'clear', 'trash']
          : ['move', 'copy', 'clear', 'trash']
        dispatch(explorer.selectActionsUpdated(actions))
      }
      break
    case 'shared':
      dispatch(explorer.initSharedView({
        disableTooltips: isTouchDevice,
        sortOrder,
        sortField
      }))
      break
    case 'trash':
      dispatch(explorer.initTrashView({
        disableTooltips: isTouchDevice,
        sortOrder,
        sortField
      }))
      break
  }
}

thunks.loadView = createAsyncThunk(
  `${sliceName}/loadView`,
  async ({ view, folderId = -1 }, { dispatch }) => {
    setupView(view, dispatch)

    // Select the appropriate entity so we can generate the proper entity ID.
    const entity = folderId === -1 ? rootFolders()[view] : stubFolder(folderId)

    await dispatch(explorer.loadItem(selectItemId(entity)))

    return view
  }, {
    condition: (_, { getState }) => {
      // don't load a view when another view is currently being loaded
      const loading = getState()[explorer.name].loading
      if (loading) {
        return false
      }
    }
  }
)

thunks.loadActivity = createAsyncThunk(
  `${sliceName}/loadActivity`,
  async ({ type, key, page }, { getState, dispatch, rejectWithValue }) => {
    const { view } = getState()[sliceName]

    try {
      const activity = await activityApi({ type: singularize(type), shared_key: key }).read({
        include: 'poll_instances'
      })

      const currentFolder = explorer.selectCurrentFolder(getState())
      const isSharedActivity = !activity.owned_by_presenter

      let entity
      if (activity.group_id && activity.group_id > 0) {
        // Wait for this to load so that the back label shows the correct name
        entity = stubFolder(activity.group_id)
      } else {
        // root folder
        entity = rootFolders()[view]
      }

      // load folder if the current folder is not correct folder
      const emptyFolder = explorer.selectTotalItems(store.getState()).length === 0
      const notShowingCorrectView = isSharedActivity && view !== 'shared'
      const differentEntity = JSON.stringify(entity) !== JSON.stringify(currentFolder)
      if (emptyFolder || notShowingCorrectView || differentEntity) {
        // load the folder for the following reasons:
        // Folder name is available in the back button
        // Prev/Next nav correctly
        // Clicking back button renders the items immediately
        await dispatch(explorer.loadItem(selectItemId(entity)))
      }

      return activity
    } catch (e) {
      const message = e.responseJSON?.error?.message || e.message || 'Unknown error occurred'
      return rejectWithValue({ status: e.status, message })
    }
  }
)

selectors.selectActivityAsModel = createSelector(
  (state) => state[sliceName].activity,
  // must assign to new object as `activity` is immutable
  (activity) => {
    if (activity && activity.id > 0) {
      const clone = cloneDeep(activity)
      return activityFactory(clone)
    } else {
      return new Skeleton({ id: -1 })
    }
  }
)

selectors.selectPresenterMode = createSelector(
  (state) => state[appSlice.name].role,
  (state) => state[sliceName].externalScreenConnected,
  (state) => state[appSlice.name].mode,
  (role, externalScreenConnected, mode) => {
    if (mode === 'integration' || role === 'slideware') {
      return 'insert'
    } else if (externalScreenConnected) {
      return 'remote'
    } else {
      return 'projector'
    }
  }
)

selectors.selectRoutePath = createSelector(
  (state) => state[sliceName].page,
  (state) => state[sliceName].view,
  explorer.selectCurrentFolder,
  selectors.selectActivityAsModel,
  (state) => state[sliceName].pageAction,
  (page, view, folder, activity, pageAction) => {
    if (page === 'activity') {
      return generateActivityPath(activity, pageAction)
    } else if (page === 'moderate') {
      return `${generateActivityPath(activity, pageAction)}/${page}`
    } else if (page === 'activities') {
      let basePath = `${page}`

      if (view && view !== 'activities') {
        basePath = `${basePath}/${view}`
      }

      if (folder && folder.id > 0) {
        basePath = `${basePath}?group_id=${folder.id}`
      }

      return basePath
    }
  }
)

const initialState = {
  page: 'activities',
  initializing: false,
  view: 'activities',

  pageScrollPosition: 0, // track current page scroll position
  pageAction: null, // page level actions like select, reorder, create, edit

  activity: null,
  folder: null,
  skeleton: null,

  // error object for 404/500 pages
  error: {}
}

const slice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    fullReset () {
      return initialState
    },
    activityUpdated (state, action) {
      state.activity = action.payload
    },
    pageUpdated (state, action) {
      state.page = action.payload
    },
    pageActionUpdated (state, action) {
      state.pageAction = action.payload
    },
    viewUpdated (state, action) {
      state.view = action.payload
    },
    folderUpdated (state, action) {
      state.folder = action.payload
    },
    errorUpdated (state, action) {
      state.error = action.payload
    }
  },
  extraReducers (builder) {
    builder
      .addCase(explorer.loadItem.rejected, (state, action) => {
        const entityId = action.meta.arg
        const { type } = selectItemIdParts(entityId)

        // handle unknown folder
        if (type === 'folder') {
          state.page = 'error'
          state.error = {
            status: 404,
            entityType: 'folder'
          }
        }
      })

      .addCase(thunks.loadView.pending, (state, action) => {
        state.initializing = true
      })
      .addCase(thunks.loadView.fulfilled, (state, action) => {
        state.view = action.payload
        state.initializing = false
      })

      .addCase(thunks.loadActivity.pending, (state, action) => {
        // don't show skeleton if we're not going to be doing any page change
        if (action.meta.arg.page === null) {
          return
        }

        state.page = 'skeleton'
        state.skeleton = 'chart'
      })
      .addCase(thunks.loadActivity.fulfilled, (state, action) => {
        state.activity = action.payload

        // don't change page if page is explicitly set to null
        if (action.meta.arg.page === null) {
          return
        }

        // gate check
        let page = action.meta.arg.page
        if (!['edit', 'moderate', 'activity', 'present'].includes(page)) {
          page = 'activity'
        }

        state.page = page
      })
      .addCase(thunks.loadActivity.rejected, (state, action) => {
        state.page = 'error'

        if (action.payload.status === 404) {
          state.error = {
            status: 404,
            entityType: 'activity'
          }
        } else {
          state.error = {
            status: 500,
            entityType: 'activity'
          }
        }
      })
  }
})

const sliceExport = {
  name: slice.name,
  reducer: slice.reducer,
  ...slice.actions,
  ...selectors,
  ...thunks
}

export { sliceExport as slice }
